import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, CreditCard, CheckCircle2, Clock, XCircle, TrendingUp, Mail, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FILTERS = [
    { key: '', label: 'Tous' },
    { key: 'paid', label: '✅ Payés' },
    { key: 'pending', label: '⏳ En cours' },
    { key: 'initiated', label: '🔵 Initiés' },
    { key: 'expired', label: '⏰ Expirés' },
    { key: 'failed', label: '❌ Échoués' },
];

const STATUS_BADGE = {
    paid: { label: 'Payé', cls: 'bg-emerald-100 text-emerald-800' },
    pending: { label: 'En cours', cls: 'bg-amber-100 text-amber-800' },
    initiated: { label: 'Initié', cls: 'bg-blue-100 text-blue-800' },
    expired: { label: 'Expiré', cls: 'bg-zinc-200 text-zinc-700' },
    failed: { label: 'Échoué', cls: 'bg-rose-100 text-rose-700' },
    cancelled: { label: 'Annulé', cls: 'bg-zinc-200 text-zinc-700' },
};

const fmtEur = (v) => (v == null ? '—' : `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`);
const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
};

export default function StripePaymentsAdmin({ auth }) {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [resending, setResending] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const q = statusFilter ? `?status=${statusFilter}` : '';
            const r = await axios.get(`${API}/super-admin/stripe-payments${q}`, auth);
            setItems(r.data.items || []);
            setStats(r.data.stats || null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Chargement échoué (schéma v23 appliqué ?)');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

    const resendActivation = async (tx) => {
        if (!window.confirm(`Renvoyer l'email d'activation à ${tx.customer_email} ?`)) return;
        setResending(tx.session_id);
        try {
            await axios.post(`${API}/super-admin/stripe-payments/${tx.session_id}/resend-activation`, {}, auth);
            toast.success(`Email d'activation renvoyé à ${tx.customer_email}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Échec du renvoi');
        } finally {
            setResending(null);
        }
    };

    return (
        <div className="space-y-6" data-testid="sa-stripe-payments">
            {/* Stats cards */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Kpi icon={<CheckCircle2 size={18} />} label="Paiements réussis" value={stats.paid_count} tint="emerald" testid="kpi-paid" />
                    <Kpi icon={<Clock size={18} />} label="En cours / initiés" value={stats.pending_count} tint="amber" testid="kpi-pending" />
                    <Kpi icon={<XCircle size={18} />} label="Échoués / expirés" value={stats.failed_count} tint="zinc" testid="kpi-failed" />
                    <Kpi icon={<TrendingUp size={18} />} label="Taux de conversion" value={`${stats.conversion_rate_pct}%`} tint="primary" testid="kpi-conv" />
                </div>
            )}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Kpi icon={<CreditCard size={18} />} label="Revenus totaux (payés)" value={fmtEur(stats.revenue_eur_total)} tint="primary" big testid="kpi-revenue-total" />
                    <Kpi icon={<CreditCard size={18} />} label="Mois en cours" value={fmtEur(stats.revenue_eur_current_month)} tint="emerald" testid="kpi-revenue-current" />
                    <Kpi icon={<CreditCard size={18} />} label="Mois précédent" value={fmtEur(stats.revenue_eur_last_month)} tint="zinc" testid="kpi-revenue-last" />
                </div>
            )}

            {/* Filters + Refresh */}
            <div className="card-soft p-5">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                    <div>
                        <h3 className="font-heading text-xl">Transactions Stripe</h3>
                        <p className="text-sm text-foreground/65 mt-1">{items.length} sur la sélection</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                            <SelectTrigger className="w-44" data-testid="stripe-status-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {STATUS_FILTERS.map(f => (
                                    <SelectItem key={f.key || 'all'} value={f.key || 'all'}>{f.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button variant="outline" size="icon" onClick={load} disabled={loading} data-testid="stripe-refresh">
                            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                        </Button>
                    </div>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="py-10 text-center text-foreground/60"><Loader2 className="inline animate-spin mr-2" size={16} /> Chargement…</div>
                ) : items.length === 0 ? (
                    <div className="py-10 text-center border-2 border-dashed rounded-lg text-foreground/55">
                        Aucune transaction pour cette sélection.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-foreground/55 border-b">
                                    <th className="py-2 pr-3">Date</th>
                                    <th className="py-2 pr-3">Acheteur</th>
                                    <th className="py-2 pr-3">Plan</th>
                                    <th className="py-2 pr-3 text-right">Montant</th>
                                    <th className="py-2 pr-3">Statut</th>
                                    <th className="py-2 pr-3">Activation</th>
                                    <th className="py-2 pr-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((tx) => {
                                    const badge = STATUS_BADGE[tx.status] || { label: tx.status, cls: 'bg-zinc-100 text-zinc-700' };
                                    return (
                                        <tr key={tx.session_id} className="border-b last:border-0 hover:bg-surface-muted/40" data-testid={`stripe-row-${tx.session_id}`}>
                                            <td className="py-2.5 pr-3 text-xs text-foreground/60 whitespace-nowrap">{fmtDate(tx.created_at)}</td>
                                            <td className="py-2.5 pr-3">
                                                <div className="font-medium">{tx.customer_name || '—'}</div>
                                                <div className="text-xs text-foreground/55">{tx.customer_email || '—'}</div>
                                            </td>
                                            <td className="py-2.5 pr-3">
                                                <div className="font-medium">{tx.plan_name}</div>
                                                <div className="text-[11px] text-foreground/50">{tx.plan_id}</div>
                                            </td>
                                            <td className="py-2.5 pr-3 text-right font-mono font-medium">{fmtEur(tx.amount)}</td>
                                            <td className="py-2.5 pr-3">
                                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.cls}`}>{badge.label}</span>
                                            </td>
                                            <td className="py-2.5 pr-3 text-xs">
                                                {tx.activated_at ? (
                                                    <span className="text-emerald-700">✓ {fmtDate(tx.activated_at)}</span>
                                                ) : tx.invitation_token ? (
                                                    <span className="text-amber-700">Lien envoyé</span>
                                                ) : (
                                                    <span className="text-foreground/40">—</span>
                                                )}
                                            </td>
                                            <td className="py-2.5 pr-3 text-right">
                                                {tx.status === 'paid' && tx.invitation_token && tx.customer_email && (
                                                    <Button variant="outline" size="sm" onClick={() => resendActivation(tx)}
                                                        disabled={resending === tx.session_id}
                                                        data-testid={`stripe-resend-${tx.session_id}`}>
                                                        {resending === tx.session_id ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                                                        <span className="ml-1.5">Renvoyer</span>
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const Kpi = ({ icon, label, value, tint, big = false, testid }) => {
    const tints = {
        emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
        amber: 'bg-amber-50 text-amber-700 ring-amber-100',
        zinc: 'bg-zinc-50 text-zinc-700 ring-zinc-200',
        primary: 'bg-primary-light text-primary ring-primary/15',
    };
    return (
        <div className={`card-soft p-4 flex items-start gap-3 ring-1 ${tints[tint] || tints.zinc}`} data-testid={testid}>
            <div className="h-9 w-9 rounded-md bg-white/60 flex items-center justify-center shrink-0">{icon}</div>
            <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
                <div className={`font-heading mt-0.5 ${big ? 'text-2xl' : 'text-xl'} text-foreground`}>{value}</div>
            </div>
        </div>
    );
};
